// src/useMicrosoftProfile.js - who is signed in
import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { supabase } from './supabaseClient';
import { capturePhotoIfPossible, cachedPhoto } from './microsoftPhoto';

const GRAPH_SCOPES = ['User.Read'];

/**
 * The signed-in admin.
 *
 * Reads the Supabase session first, because that is where the identity
 * actually lives. Sign-in goes through supabase.auth.signInWithOAuth in
 * MicrosoftLogin.js, not MSAL's loginPopup, so useMsal().accounts is empty and
 * anything built on it could never resolve. That is why the header said "Admin"
 * for everyone: the previous version asked MSAL, MSAL had nobody, and the
 * fallback string won. MSAL's cacheLocation is sessionStorage as well, so even
 * a real MSAL login would not survive opening a new tab.
 *
 * The session carries display_name, first_name and last_name in user_metadata.
 * It does not carry a picture, so `photoUrl` stays null unless an MSAL account
 * happens to exist and Graph answers — which is left in place because it costs
 * nothing and starts working by itself if sign-in ever moves to MSAL.
 */
export default function useMicrosoftProfile() {
  const { instance, accounts } = useMsal();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const user = data?.session?.user;

        if (!user) {
          if (!cancelled) setLoading(false);
          return;
        }

        const meta = user.user_metadata || {};
        const first = meta.first_name || '';
        const last = meta.last_name || '';
        const display =
          meta.display_name ||
          meta.full_name ||
          meta.name ||
          `${first} ${last}`.trim() ||
          user.email ||
          '';

        const base = {
          firstName: first,
          lastName: last,
          displayName: display,
          email: (meta.email || user.email || '').toLowerCase(),
          jobTitle: '',
          // Whatever was stored when they last signed in. Graph cannot be
          // reached now, because provider_token only exists on the sign-in
          // response, so this is the photo or there is none.
          photoUrl: cachedPhoto(user.id),
        };

        if (!cancelled) {
          setProfile(base);
          setLoading(false);
        }

        // Covers the load right after the OAuth redirect, where the session in
        // hand still carries the token and nothing has stored the photo yet.
        if (!base.photoUrl && data?.session?.provider_token) {
          const fetched = await capturePhotoIfPossible(data.session);
          if (fetched && !cancelled) {
            setProfile(prev => ({ ...(prev || base), photoUrl: fetched }));
          }
        }

        // Only if an MSAL account exists. It does not today, and this is a
        // no-op rather than an error in that case.
        const account = accounts?.[0];
        if (!account) return;

        let token;
        try {
          const res = await instance.acquireTokenSilent({ scopes: GRAPH_SCOPES, account });
          token = res.accessToken;
        } catch (err) {
          if (err instanceof InteractionRequiredAuthError) return; // never prompt just for a picture
          throw err;
        }

        const headers = { Authorization: `Bearer ${token}` };
        const [meRes, photoRes] = await Promise.all([
          fetch('https://graph.microsoft.com/v1.0/me', { headers }),
          fetch('https://graph.microsoft.com/v1.0/me/photo/$value', { headers }).catch(() => null),
        ]);
        if (cancelled) return;

        const next = { ...base };
        if (meRes?.ok) {
          const g = await meRes.json();
          next.firstName = g.givenName || next.firstName;
          next.lastName = g.surname || next.lastName;
          next.displayName = g.displayName || next.displayName;
          next.jobTitle = g.jobTitle || '';
        }
        // A 404 only means no picture was ever set, which is ordinary.
        if (photoRes?.ok) {
          const blob = await photoRes.blob();
          if (!cancelled && blob.size > 0) {
            objectUrl = URL.createObjectURL(blob);
            next.photoUrl = objectUrl;
          }
        }
        if (!cancelled) setProfile(next);
      } catch (err) {
        console.warn('Could not load the signed-in profile:', err);
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      // The object URL pins the image in memory until it is revoked.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [instance, accounts]);

  return { profile, loading };
}
